// Budget API Service
import { request } from "./apiService";
import { getBookings } from "./azureBookingService";
import { calculateMwstAmounts } from "./mwstService";

const API_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "budget",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Separate config for BudgetTargets table
const BUDGET_TARGETS_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "budgetTargets",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// ========== BUDGET TARGETS FUNCTIONS ==========

// Create the budgetTargets table if it doesn't exist
const ensureBudgetTargetsTableExists = async () => {
  try {
    const url = `https://${BUDGET_TARGETS_CONFIG.storageAccount}.table.core.windows.net/Tables?${BUDGET_TARGETS_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: BUDGET_TARGETS_CONFIG.tableName }),
    });

    // 201 = Created, 409 = Already exists (both are fine)
    if (response.ok || response.status === 409) {
      return true;
    }

    console.warn("Could not create budgetTargets table:", response.status);
    return false;
  } catch (error) {
    console.warn("Error creating budgetTargets table:", error);
    return false;
  }
};

// Load all budget targets for a year
export const getBudgetTargets = async (year) => {
  try {
    const baseUrl = `https://${BUDGET_TARGETS_CONFIG.storageAccount}.table.core.windows.net/${BUDGET_TARGETS_CONFIG.tableName}`;
    const filter = `&$filter=PartitionKey eq '${year}'`;

    let allTargets = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}?${BUDGET_TARGETS_CONFIG.sasToken}${filter}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allTargets = allTargets.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    // Transform to a simple object: { categoryName: amount }
    const targets = {};
    allTargets.forEach((item) => {
      targets[item.RowKey] = item.Amount || 0;
    });

    return targets;
  } catch (error) {
    console.error("Fehler beim Laden der Budget-Zielwerte:", error);
    return {};
  }
};

// Save a budget target for a category
export const saveBudgetTarget = async (year, category, amount) => {
  try {
    // Ensure the table exists first
    await ensureBudgetTargetsTableExists();

    const url = `https://${BUDGET_TARGETS_CONFIG.storageAccount}.table.core.windows.net/${BUDGET_TARGETS_CONFIG.tableName}(PartitionKey='${year}',RowKey='${encodeURIComponent(category)}')?${BUDGET_TARGETS_CONFIG.sasToken}`;

    const entity = {
      PartitionKey: String(year),
      RowKey: category,
      Amount: parseFloat(amount) || 0,
      UpdatedAt: new Date().toISOString(),
    };

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }

    return { year, category, amount };
  } catch (error) {
    console.error("Fehler beim Speichern des Budget-Zielwerts:", error);
    throw error;
  }
};

// ========== CASHFLOW-WARNSCHWELLE (Einstellung, budgetTargets-Tabelle) ==========
// Gespeichert wird ein fixer CHF-Wert; ist keiner gespeichert (null), gilt der
// dynamische Standard: 3 x durchschnittliche regelmässige Monatsausgaben.

// Durchschnittliche Monatsausgaben: Jahresbetrag aller regelmässigen Ausgaben / 12.
// Basis sind die HEUTE aktiven Serien (abgelaufene und noch nicht gestartete
// zählen nicht — sonst zählen Ketten [alter Betrag bis X / neuer ab X] doppelt).
// Ausnahme: 13.-Monatslohn-Serien (*_13) zählen immer mit, auch wenn ihr
// ab_wann (nächster November) in der Zukunft liegt.
export const calculateAverageMonthlyRecurringExpenses = (entries) => {
  const today = new Date().toISOString().split("T")[0];
  let yearly = 0;

  for (const e of entries) {
    if (e.typ !== "Ausgabe" || e.typ_timing !== "Regelmässig") continue;
    if (e.bis_wann && e.bis_wann < today) continue;
    const ist13 = (e.lohn_eintrag_id || "").endsWith("_13");
    if (e.ab_wann && e.ab_wann > today && !ist13) continue;

    const betrag = e.brutto_betrag || e.betrag || 0;
    if (e.haeufigkeit === "monatlich") yearly += betrag * 12;
    else if (e.haeufigkeit === "quartalsweise") yearly += betrag * 4;
    else if (e.haeufigkeit === "jährlich") yearly += betrag;
  }

  return Math.round((yearly / 12) * 100) / 100;
};

const WARNSCHWELLE_URL = () =>
  `https://${BUDGET_TARGETS_CONFIG.storageAccount}.table.core.windows.net/${BUDGET_TARGETS_CONFIG.tableName}(PartitionKey='settings',RowKey='cashflowWarnschwelle')?${BUDGET_TARGETS_CONFIG.sasToken}`;

// Gespeicherte Warnschwelle laden (null = kein fixer Wert -> Standard gilt)
export const getCashflowWarnschwelle = async () => {
  try {
    const response = await fetch(WARNSCHWELLE_URL(), {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (response.status === 404) return null;
    if (!response.ok) return null;
    const item = await response.json();
    const value = parseFloat(item.Amount);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

// Warnschwelle speichern; null/0 löscht den fixen Wert (zurück zum Standard)
export const saveCashflowWarnschwelle = async (value) => {
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num <= 0) {
    const response = await fetch(WARNSCHWELLE_URL(), {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Warnschwelle löschen fehlgeschlagen: ${response.status}`);
    }
    return null;
  }

  await ensureBudgetTargetsTableExists();
  const entity = {
    PartitionKey: "settings",
    RowKey: "cashflowWarnschwelle",
    Amount: num,
    UpdatedAt: new Date().toISOString(),
  };
  const response = await fetch(WARNSCHWELLE_URL(), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    throw new Error(`Warnschwelle speichern fehlgeschlagen: ${response.status}`);
  }
  return num;
};

// Basis-URL für Azure Table Storage
const getBaseUrl = () =>
  `https://${API_CONFIG.storageAccount}.table.core.windows.net/${API_CONFIG.tableName}`;

// Alle Budget-Einträge laden (mit Paginierung)
export const getBudgetEntries = async () => {
  try {
    const baseUrl = getBaseUrl();
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}?${API_CONFIG.sasToken}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: {
          Accept: "application/json;odata=nometadata",
        },
      });

      if (!response.ok) {
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      // Check for continuation headers
      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");

    } while (nextPartitionKey && nextRowKey);

    // Transformiere Azure Table Storage Format zu unserem Format
    return allEntries.map((item) => {
      const entry = {
        id: item.RowKey,
        typ: item.typ || "Ausgabe",
        name: item.name || "",
        konto: item.konto || "",
        typ_timing: item.typ_timing || "Einmalig",
        ab_wann: item.ab_wann || "",
        bis_wann: item.bis_wann || "",
        haeufigkeit: item.haeufigkeit || "monatlich",
        wann: item.wann || "",
        betrag: parseFloat(item.betrag) || 0,
        projekt: item.projekt || "",
        phase: item.phase || "",
        partner_id: item.partner_id || "",
        partner_name: item.partner_name || "",

        // MwSt-Felder (mit Fallback für bestehende Einträge)
        mwst_satz:
          item.mwst_satz !== undefined ? parseFloat(item.mwst_satz) : 8.1,
        ist_brutto: item.ist_brutto !== false,
        ist_prognose: item.ist_prognose === true,
        brutto_betrag: parseFloat(item.brutto_betrag) || 0,
        netto_betrag:
          parseFloat(item.netto_betrag) || parseFloat(item.betrag) || 0,
        mwst_betrag: parseFloat(item.mwst_betrag) || 0,

        // Status-Tracking Felder (NEU für Overdue-Tracking)
        // Default status: "expected" for income, "expecting" for expenses
        status:
          item.status || (item.typ === "Einnahme" ? "expected" : "expecting"),
        sent_date: item.sent_date || null,
        paid_date: item.paid_date || null,
        matched_booking_id: item.matched_booking_id || null,

        // Zahlungsfrist-Felder (NEU)
        versand_datum: item.versand_datum || null,
        zahlungsfrist: item.zahlungsfrist || null,

        // Ausgaben-spezifische Felder
        automatic_payment: item.automatic_payment === true,
        payment_date: item.payment_date || null, // Datum für "Zahlung ausgelöst"

        // Recurring instance tracking
        created_from_recurring_id: item.created_from_recurring_id || null,
        instance_period: item.instance_period || null, // Format: "2025-11" for November 2025

        // Verknuepfung mit Stundenauszug
        invoice_reference: item.invoice_reference || "",
        linked_invoiced_period_id: item.linked_invoiced_period_id || "",

        // Lohn-Verknuepfung
        team_member_id: item.team_member_id || "",
        lohn_eintrag_id: item.lohn_eintrag_id || "",
        deletable_with_member: item.deletable_with_member === true,

        // Anzahl x Stueckbetrag (regelmaessige Ausgaben, z.B. Abos: 3 x Adobe)
        // betrag bleibt IMMER der Gesamtbetrag; diese Felder sind Anzeige/Editier-Hilfe
        anzahl: item.anzahl !== undefined && item.anzahl !== "" ? parseInt(item.anzahl) : null,
        stueckbetrag:
          item.stueckbetrag !== undefined && item.stueckbetrag !== ""
            ? parseFloat(item.stueckbetrag)
            : null,

        // Herkunft (z.B. "zahlungsplan")
        source: item.source || "",

        // Beleg-Verknüpfung (Zahlungsplan, PDF-Beleg in projectBelege)
        beleg_id: item.beleg_id || "",

        created_at: item.Timestamp,
        updated_at: item.UpdatedAt || item.Timestamp,
      };

      // Berechne MwSt-Werte neu falls sie fehlen (Migration bestehender Daten)
      if (!item.mwst_betrag || !item.brutto_betrag) {
        const mwstCalculation = calculateMwstAmounts(
          entry.betrag,
          entry.mwst_satz,
          entry.ist_brutto
        );

        entry.brutto_betrag = mwstCalculation.bruttoBetrag;
        entry.netto_betrag = mwstCalculation.nettoBetrag;
        entry.mwst_betrag = mwstCalculation.mwstBetrag;
      }

      return entry;
    });
  } catch (error) {
    console.error("Fehler beim Laden der Budget-Einträge mit MwSt:", error);
    return [];
  }
};

// Budget-Einträge für ein bestimmtes Projekt laden.
// Matching mit Ziffern-Grenze statt includes(): Substring-Matching zog bei
// dreistelligen Codes fremde Einträge ins Projekt — "025" matchte jeden Namen
// mit "2025" (Jahreszahlen!) und Projekt "1025". Mit Grenze matcht "025 - X",
// "X 025", "025/026", aber nicht "2025"/"1025" (Fix 2026-07-24, siehe
// docs/lessons-learned.md).
export const getBudgetEntriesForProject = async (projectCode) => {
  const allEntries = await getBudgetEntries();
  const escaped = String(projectCode).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const codeWithBoundary = new RegExp(`(^|\\D)${escaped}(\\D|$)`);
  const normalizedCode = projectCode.replace(/^0+/, '');
  return allEntries.filter((b) => {
    if (b.projekt === projectCode) return true;
    if (b.projekt && codeWithBoundary.test(b.projekt)) return true;
    if (b.name && codeWithBoundary.test(b.name)) return true;
    if (b.projekt && b.projekt.replace(/^0+/, '') === normalizedCode) return true;
    return false;
  });
};

// Budget-Eintrag speichern (neu oder update)
export const saveBudgetEntry = async (entryData) => {
  try {
    // MwSt-Beträge berechnen
    const mwstCalculation = calculateMwstAmounts(
      parseFloat(entryData.betrag),
      parseFloat(entryData.mwst_satz ?? 8.1),
      entryData.ist_brutto !== false // Default: true
    );

    // wann = Rechnungsdatum (Datum der Rechnungsstellung)
    // Fälligkeitsdatum wird in der Cashflow-Ansicht dynamisch berechnet (wann + zahlungsfrist)
    let calculatedWann = entryData.wann || "";

    const rowKey = entryData.id || generateId();
    const url = `${getBaseUrl()}(PartitionKey='budget',RowKey='${rowKey}')?${
      API_CONFIG.sasToken
    }`;

    const azureData = {
      PartitionKey: "budget",
      RowKey: rowKey,
      typ: entryData.typ,
      name: entryData.name,
      konto: entryData.konto || "",
      typ_timing: entryData.typ_timing,
      ab_wann: entryData.ab_wann || "",
      bis_wann: entryData.bis_wann || "",
      haeufigkeit: entryData.haeufigkeit || "monatlich",
      wann: calculatedWann,
      betrag: mwstCalculation.nettoBetrag, // Speichere Netto als Basis
      projekt: entryData.projekt || "",
      phase: entryData.phase || "",
      partner_id: entryData.partner_id || "",
      partner_name: entryData.partner_name || "",

      // Neue MwSt-Felder
      mwst_satz: mwstCalculation.mwstSatz,
      ist_brutto: entryData.ist_brutto !== false,
      ist_prognose: entryData.ist_prognose === true,
      brutto_betrag: mwstCalculation.bruttoBetrag,
      netto_betrag: mwstCalculation.nettoBetrag,
      mwst_betrag: mwstCalculation.mwstBetrag,

      // Status-Tracking Felder
      // Default typ-abhaengig wie beim Laden (Ausgaben: "expecting"): der
      // fruehere pauschale "expected"-Default liess Ausgaben durch alle
      // "expecting"-Filter fallen (Overdue-Erkennung, Mover, Aggregat-Update)
      status:
        entryData.status || (entryData.typ === "Einnahme" ? "expected" : "expecting"),
      sent_date: entryData.sent_date || "",
      paid_date: entryData.paid_date || "",
      matched_booking_id: entryData.matched_booking_id || "",

      // Zahlungsfrist Felder
      versand_datum: entryData.versand_datum || "",
      zahlungsfrist: entryData.zahlungsfrist
        ? Math.max(0, Math.min(999, parseInt(entryData.zahlungsfrist) || 0))
        : "",

      // Ausgaben-spezifische Felder
      automatic_payment: entryData.automatic_payment === true,
      payment_date: entryData.payment_date || "",

      // Recurring instance tracking
      created_from_recurring_id: entryData.created_from_recurring_id || "",
      instance_period: entryData.instance_period || "",

      // Verknuepfung mit Stundenauszug
      invoice_reference: entryData.invoice_reference || "",
      linked_invoiced_period_id: entryData.linked_invoiced_period_id || "",

      // Lohn-Verknuepfung
      team_member_id: entryData.team_member_id || "",
      lohn_eintrag_id: entryData.lohn_eintrag_id || "",
      deletable_with_member: entryData.deletable_with_member === true,

      // Anzahl x Stueckbetrag (regelmaessige Ausgaben)
      anzahl:
        entryData.anzahl != null && entryData.anzahl !== ""
          ? parseInt(entryData.anzahl) || ""
          : "",
      stueckbetrag:
        entryData.stueckbetrag != null && entryData.stueckbetrag !== ""
          ? parseFloat(entryData.stueckbetrag) || ""
          : "",

      // Herkunft (z.B. "zahlungsplan")
      source: entryData.source || "",

      // Beleg-Verknüpfung (Zahlungsplan, PDF-Beleg in projectBelege)
      beleg_id: entryData.beleg_id || "",

      UpdatedAt: new Date().toISOString(),
    };

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(azureData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }

    // Rueckgabe in der FORM DES LADENS (snake_case): entryData kann alte
    // brutto_/netto_betrag-Werte enthalten (z.B. aus gespreadetem editingEntry)
    // und mwstCalculation liefert nur camelCase — ohne die expliziten Felder
    // unten zeigten Aufrufer nach dem Speichern veraltete Betraege an.
    return {
      id: rowKey,
      ...entryData,
      ...mwstCalculation,
      betrag: mwstCalculation.nettoBetrag,
      brutto_betrag: mwstCalculation.bruttoBetrag,
      netto_betrag: mwstCalculation.nettoBetrag,
      mwst_betrag: mwstCalculation.mwstBetrag,
      mwst_satz: mwstCalculation.mwstSatz,
      status: azureData.status,
      anzahl: azureData.anzahl === "" ? null : azureData.anzahl,
      stueckbetrag: azureData.stueckbetrag === "" ? null : azureData.stueckbetrag,
    };
  } catch (error) {
    console.error("Fehler beim Speichern des Budget-Eintrags mit MwSt:", error);
    throw error;
  }
};

// Budget-Eintrag löschen
export const deleteBudgetEntry = async (id) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='budget',RowKey='${id}')?${
      API_CONFIG.sasToken
    }`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Accept: "application/json;odata=nometadata",
        "If-Match": "*", // Erforderlich für DELETE in Azure Table Storage
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Budget-Eintrags:", error);
    throw error;
  }
};

// Betrag/Anzahl einer regelmässigen Ausgabe ab einem Datum ändern (Kette wie
// bei den Lohn-Budgets): der bestehende Eintrag läuft bis zum Vortag, ab dem
// Datum übernimmt ein NEUER Eintrag mit dem neuen Betrag. Liegt das Datum am
// oder vor dem Beginn des Eintrags, wird direkt aktualisiert (keine Kette).
// betrag = Gesamtbetrag (bei ist_brutto inkl. MwSt, wie im Formular).
export const changeRecurringEntryFromDate = async (
  entry,
  { abDatum, betrag, anzahl, stueckbetrag }
) => {
  if (!entry?.id || entry.typ_timing !== "Regelmässig") {
    throw new Error("changeRecurringEntryFromDate: nur für regelmässige Einträge");
  }
  if (!abDatum) {
    throw new Error("changeRecurringEntryFromDate: abDatum fehlt");
  }

  const neueWerte = {
    betrag,
    anzahl: anzahl != null ? anzahl : entry.anzahl ?? "",
    stueckbetrag: stueckbetrag != null ? stueckbetrag : entry.stueckbetrag ?? "",
  };

  // Änderung beginnt am/vor dem Start des Eintrags -> in place aktualisieren
  if (!entry.ab_wann || abDatum <= entry.ab_wann) {
    return saveBudgetEntry({ ...entry, ...neueWerte });
  }

  // 1) Bestehenden Eintrag per Vortag schliessen (UTC-sicher via ISO-Datum)
  const vortag = new Date(`${abDatum}T00:00:00Z`);
  vortag.setUTCDate(vortag.getUTCDate() - 1);
  const bisAlt = vortag.toISOString().split("T")[0];
  await saveBudgetEntry({ ...entry, bis_wann: bisAlt });

  // 2) Neuen Eintrag ab dem Datum anlegen (Status-/Zahlungsfelder frisch)
  const { id, created_at, updated_at, ...basis } = entry;
  return saveBudgetEntry({
    ...basis,
    ...neueWerte,
    ab_wann: abDatum,
    bis_wann: entry.bis_wann || "",
    status: "",
    sent_date: "",
    paid_date: "",
    payment_date: "",
    matched_booking_id: "",
    created_from_recurring_id: "",
    instance_period: "",
  });
};

// Budget-Eintrag Status aktualisieren (NEU für Overdue-Tracking)
export const updateBudgetEntryStatus = async (
  id,
  status,
  additionalData = {}
) => {
  try {
    // Lade aktuellen Eintrag
    const entries = await getBudgetEntries();
    const currentEntry = entries.find((e) => e.id === id);

    if (!currentEntry) {
      throw new Error(`Budget-Eintrag mit ID ${id} nicht gefunden`);
    }

    // Merge mit zusätzlichen Daten (sent_date, paid_date, matched_booking_id)
    const updatedEntry = {
      ...currentEntry,
      status,
      ...additionalData,
    };

    // Speichere mit saveBudgetEntry
    return await saveBudgetEntry(updatedEntry);
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Budget-Status:", error);
    throw error;
  }
};

// Budget-Einträge für Zukunftsprognose laden (für Chart-Integration)
export const getBudgetProjections = async (startDate, endDate) => {
  try {
    const entries = await getBudgetEntries();
    const projections = [];

    entries.forEach((entry) => {
      if (entry.typ_timing === "Einmalig") {
        // Einmalige Einträge
        const dateStr = entry.versand_datum || entry.wann;
        const entryDate = new Date(dateStr);
        if (
          entryDate >= new Date(startDate) &&
          entryDate <= new Date(endDate)
        ) {
          projections.push({
            date: dateStr,
            typ: entry.typ,
            name: entry.name,
            betrag: entry.brutto_betrag,
            netto_betrag: entry.netto_betrag,
            mwst_betrag: entry.mwst_betrag,
            mwst_satz: entry.mwst_satz,
            projekt: entry.projekt,
            konto: entry.konto,
          });
        }
      } else {
        // Regelmässige Einträge - berechne alle Vorkommen im Zeitraum
        const startEntryDate = new Date(entry.ab_wann);
        const endEntryDate = entry.bis_wann
          ? new Date(entry.bis_wann)
          : new Date(endDate);
        const rangeStart = new Date(
          Math.max(startEntryDate.getTime(), new Date(startDate).getTime())
        );
        const rangeEnd = new Date(
          Math.min(endEntryDate.getTime(), new Date(endDate).getTime())
        );

        const occurrences = calculateRecurringOccurrences(
          rangeStart,
          rangeEnd,
          entry.haeufigkeit
        );

        occurrences.forEach((date) => {
          projections.push({
            date: date.toISOString().split("T")[0],
            typ: entry.typ,
            name: entry.name,
            betrag: entry.brutto_betrag, // Verwende Brutto für Cashflow
            netto_betrag: entry.netto_betrag,
            mwst_betrag: entry.mwst_betrag,
            mwst_satz: entry.mwst_satz,
            projekt: entry.projekt,
            konto: entry.konto,
          });
        });
      }
    });

    return projections.sort((a, b) => new Date(a.date) - new Date(b.date));
  } catch (error) {
    console.error(
      "Fehler beim Berechnen der Budget-Prognosen mit MwSt:",
      error
    );
    return [];
  }
};

// Hilfsfunktion: Berechne Vorkommen für regelmässige Einträge
const calculateRecurringOccurrences = (startDate, endDate, frequency) => {
  const occurrences = [];
  const origDay = startDate.getDate();
  let monthOffset = 0;

  while (true) {
    // Always compute from the original start date to avoid day drift
    let nextDate;
    switch (frequency) {
      case "monatlich":
        nextDate = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
        nextDate.setDate(Math.min(origDay, new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate()));
        monthOffset += 1;
        break;
      case "quartalsweise":
        nextDate = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
        nextDate.setDate(Math.min(origDay, new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate()));
        monthOffset += 3;
        break;
      case "jährlich":
        nextDate = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
        nextDate.setDate(Math.min(origDay, new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate()));
        monthOffset += 12;
        break;
      default:
        return occurrences;
    }

    if (nextDate > endDate) break;
    occurrences.push(nextDate);
  }

  return occurrences;
};

// ID-Generator für neue Einträge
const generateId = () => {
  return "budget_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
};

export const getKonten = async () => {
  try {
    const bookings = await getBookings();
    const kontenMap = new Map();

    bookings.forEach((booking) => {
      // Für Einnahmen: Gegenkonto >= 3000 und < 4000
      if (booking.Gegenkonto >= 3000 && booking.Gegenkonto < 4000) {
        const kontoId = booking.Gegenkonto.toString();
        if (!kontenMap.has(kontoId)) {
          kontenMap.set(kontoId, {
            id: kontoId,
            name: `${booking.Gegenkonto} - ${
              booking.GegenkontoBezeichnung || "Unbekannt"
            }`,
            bezeichnung: booking.GegenkontoBezeichnung || "Unbekannt",
            kategorie: "Einnahmen",
          });
        }
      }

      // Für Ausgaben: Konto >= 4000
      if (booking.Konto >= 4000) {
        const kontoId = booking.Konto.toString();
        if (!kontenMap.has(kontoId)) {
          kontenMap.set(kontoId, {
            id: kontoId,
            name: `${booking.Konto} - ${
              booking.KontoBezeichnung || "Unbekannt"
            }`,
            bezeichnung: booking.KontoBezeichnung || "Unbekannt",
            kategorie: "Ausgaben",
          });
        }
      }
    });

    // Konvertiere Map zu Array und sortiere
    const konten = Array.from(kontenMap.values()).sort((a, b) => {
      // Erst nach Kategorie, dann nach Kontonummer
      if (a.kategorie !== b.kategorie) {
        return a.kategorie === "Einnahmen" ? -1 : 1;
      }
      return parseInt(a.id) - parseInt(b.id);
    });

    return konten;
  } catch (error) {
    console.error("Fehler beim Laden der Konten aus Bookings:", error);

    // Fallback: Standard-Kontenplan
    return [
      // Einnahmen-Konten (3000-3999)
      {
        id: "3000",
        name: "3000 - Dienstleistungsertrag",
        kategorie: "Einnahmen",
      },
      { id: "3100", name: "3100 - Produktverkauf", kategorie: "Einnahmen" },
      { id: "3200", name: "3200 - Lizenzertrag", kategorie: "Einnahmen" },
      { id: "3300", name: "3300 - Beratungsertrag", kategorie: "Einnahmen" },
      { id: "3400", name: "3400 - Wartungsertrag", kategorie: "Einnahmen" },

      // Ausgaben-Konten (4000+)
      { id: "4000", name: "4000 - Materialaufwand", kategorie: "Ausgaben" },
      { id: "5000", name: "5000 - Personalaufwand", kategorie: "Ausgaben" },
      {
        id: "5100",
        name: "5100 - Sozialversicherungen",
        kategorie: "Ausgaben",
      },
      { id: "6000", name: "6000 - Raumaufwand", kategorie: "Ausgaben" },
      { id: "6100", name: "6100 - Unterhalt", kategorie: "Ausgaben" },
      { id: "6200", name: "6200 - Büroaufwand", kategorie: "Ausgaben" },
      { id: "6300", name: "6300 - Informatikaufwand", kategorie: "Ausgaben" },
      { id: "6400", name: "6400 - Marketing", kategorie: "Ausgaben" },
      { id: "6500", name: "6500 - Fahrzeugaufwand", kategorie: "Ausgaben" },
      { id: "6600", name: "6600 - Versicherungen", kategorie: "Ausgaben" },
      { id: "6700", name: "6700 - Abschreibungen", kategorie: "Ausgaben" },
    ];
  }
};

// Zusätzliche Hilfsfunktion: Konten nach Kategorie filtern
export const getKontenByKategorie = async (kategorie) => {
  const alleKonten = await getKonten();
  return alleKonten.filter((konto) => konto.kategorie === kategorie);
};

// Hilfsfunktion: Konto-Details abrufen
export const getKontoDetails = async (kontoId) => {
  const alleKonten = await getKonten();
  return alleKonten.find((konto) => konto.id === kontoId);
};

// Projekte für Dropdown laden (erweitert bestehenden apiService)
export const getProjekte = async () => {
  try {
    // Versuche echte Projektdaten zu laden
    const contracts = await request(
      "/accounts/[ACCOUNT_UUID]/documents/v2/contract-notes"
    );

    return contracts.data.documents
      .filter((doc) => !doc.isArchived && !doc.isCancelled)
      .map((doc) => ({
        id: doc.reference || doc.uuid,
        name: doc.reference
          ? `${doc.reference} - ${
              doc.title || doc.receiver?.name || "Unbekannt"
            }`
          : doc.title || doc.receiver?.name || "Unbekannt",
      }));
  } catch (error) {
    // Fallback: Mock-Daten
    console.warn("Konnte Projekte nicht laden, verwende Mock-Daten:", error);
    return [
      { id: "P001", name: "P001 - Website Redesign" },
      { id: "P002", name: "P002 - Mobile App" },
      { id: "P003", name: "P003 - Marketing Campaign" },
      { id: "P004", name: "P004 - System Integration" },
      { id: "P005", name: "P005 - Data Migration" },
    ];
  }
};

// Projekt-Auswertung: Einnahmen und Ausgaben nach Projekt
export const getProjectAnalysis = async (
  year = new Date().getFullYear(),
  quarter = null
) => {
  try {
    const entries = await getBudgetEntries();
    const today = new Date();
    const endOfYear = new Date(year, 11, 31, 23, 59, 59);

    const analysis = {
      income: {
        byProject: {},
        totalActual: 0,
        totalForecast: 0,
        total: 0,
      },
      expense: {
        byProject: {},
        totalActual: 0,
        totalForecast: 0,
        total: 0,
      },
    };

    entries.forEach((entry) => {
      // Detect project code from either projekt field or name field
      let projectKey = entry.projekt;

      // If no explicit projekt field, try to extract from name
      if (!projectKey || projectKey.trim() === "") {
        const nameMatch = entry.name?.match(/\b(\d{3})\b/); // Match 3-digit codes
        if (nameMatch) {
          projectKey = nameMatch[1];
        }
      }

      // Only process entries with a project (either explicit or detected)
      if (!projectKey || projectKey.trim() === "") return;

      // Zeitfilter
      let matchesTimeFilter = false;
      const entryYear =
        entry.typ_timing === "Einmalig"
          ? new Date(entry.versand_datum || entry.wann).getFullYear()
          : new Date(entry.ab_wann).getFullYear();

      if (entryYear !== year) return;

      if (quarter) {
        const startMonth = (quarter - 1) * 3;
        const endMonth = quarter * 3 - 1;

        if (entry.typ_timing === "Einmalig") {
          const month = new Date(entry.versand_datum || entry.wann).getMonth();
          matchesTimeFilter = month >= startMonth && month <= endMonth;
        } else {
          matchesTimeFilter = true; // Regelmässige Einträge berücksichtigen
        }
      } else {
        matchesTimeFilter = true;
      }

      if (!matchesTimeFilter) return;
      const betrag = entry.brutto_betrag || entry.betrag || 0;
      const isIncome = entry.typ === "Einnahme";
      const targetSection = isIncome ? analysis.income : analysis.expense;

      // Initialisiere Projekt wenn nötig
      if (!targetSection.byProject[projectKey]) {
        targetSection.byProject[projectKey] = {
          actual: 0,
          forecast: 0,
          total: 0,
          byPhase: {}, // NEU: Phase breakdown
        };
      }

      // Track phase breakdown for both income and expenses
      if (entry.phase) {
        const phaseKey = entry.phase;
        if (!targetSection.byProject[projectKey].byPhase[phaseKey]) {
          targetSection.byProject[projectKey].byPhase[phaseKey] = {
            actual: 0,
            forecast: 0,
            total: 0,
          };
        }
      }

      // Berechne ob Ist oder Prognose
      if (entry.typ_timing === "Einmalig") {
        const entryDate = new Date(entry.versand_datum || entry.wann);
        const isPast = entryDate <= today;

        if (isPast) {
          targetSection.byProject[projectKey].actual += betrag;
          targetSection.totalActual += betrag;

          if (entry.phase) {
            targetSection.byProject[projectKey].byPhase[entry.phase].actual += betrag;
          }
        } else if (entryDate <= endOfYear) {
          targetSection.byProject[projectKey].forecast += betrag;
          targetSection.totalForecast += betrag;

          if (entry.phase) {
            targetSection.byProject[projectKey].byPhase[entry.phase].forecast += betrag;
          }
        }

        if (entryDate <= endOfYear) {
          targetSection.byProject[projectKey].total += betrag;
          targetSection.total += betrag;

          if (entry.phase) {
            targetSection.byProject[projectKey].byPhase[entry.phase].total += betrag;
          }
        }
      } else {
        // Regelmässige Einträge
        const startDate = new Date(entry.ab_wann);
        const endDate = entry.bis_wann ? new Date(entry.bis_wann) : endOfYear;

        const rangeStart = new Date(
          Math.max(startDate.getTime(), new Date(year, 0, 1).getTime())
        );
        const rangeEnd = new Date(
          Math.min(endDate.getTime(), endOfYear.getTime())
        );

        if (quarter) {
          const qStart = new Date(year, (quarter - 1) * 3, 1);
          const qEnd = new Date(year, quarter * 3, 0, 23, 59, 59);
          rangeStart.setTime(Math.max(rangeStart.getTime(), qStart.getTime()));
          rangeEnd.setTime(Math.min(rangeEnd.getTime(), qEnd.getTime()));
        }

        const occurrences = calculateRecurringOccurrences(
          rangeStart,
          rangeEnd,
          entry.haeufigkeit
        );

        occurrences.forEach((occDate) => {
          const isPast = occDate <= today;

          if (isPast) {
            targetSection.byProject[projectKey].actual += betrag;
            targetSection.totalActual += betrag;
          } else {
            targetSection.byProject[projectKey].forecast += betrag;
            targetSection.totalForecast += betrag;
          }

          targetSection.byProject[projectKey].total += betrag;
          targetSection.total += betrag;
        });
      }
    });

    return analysis;
  } catch (error) {
    console.error("Fehler bei Projekt-Auswertung:", error);
    return {
      income: { byProject: {}, totalActual: 0, totalForecast: 0, total: 0 },
      expense: { byProject: {}, totalActual: 0, totalForecast: 0, total: 0 },
    };
  }
};

// Kategorien-Auswertung aus Budget-Einträgen (für Prognosen)
// Kategorien-Auswertung aus Budget-Einträgen (für Prognosen)
export const getCategoryForecastAnalysis = async (
  year = new Date().getFullYear(),
  quarter = null
) => {
  try {
    const entries = await getBudgetEntries();
    const today = new Date();
    const endOfYear = new Date(year, 11, 31, 23, 59, 59);

    const analysis = {
      income: {
        byCategory: {},
        totalActual: 0,
        totalForecast: 0,
        total: 0,
      },
      expense: {
        byCategory: {},
        totalActual: 0,
        totalForecast: 0,
        total: 0,
      },
    };

    // Konto-zu-Kategorie Mapping (gleich wie in azureBookingService)
    const getExpenseCategory = (konto, projekt = null) => {
      if (!konto) return "Sonstige Ausgaben";
      const kontoNum = parseInt(konto);

      // If expense is assigned to a project, categorize as Subplaner or Projektkosten
      if (projekt) {
        if (kontoNum === 4400) return "Subplaner";
        return "Projektkosten";
      }

      if (kontoNum >= 4000 && kontoNum < 5000) return "Material";
      if (kontoNum >= 5000 && kontoNum < 6000) return "Lohn";
      if (kontoNum >= 5700 && kontoNum < 5800) return "Sozialversicherungen";
      if (kontoNum >= 5800 && kontoNum < 5900) return "Verpflegung";
      if (kontoNum >= 6000 && kontoNum < 6100) return "Raumaufwand";
      if (kontoNum >= 6500 && kontoNum < 6600 && kontoNum !== 6580)
        return "Büromaterial";
      if (kontoNum === 6580) return "Lizenzen";
      if (kontoNum === 6900) return "Bankspesen";
      if (kontoNum >= 6700 && kontoNum < 6800) return "Abschreibungen";
      return "Sonstige Ausgaben";
    };

    const getIncomeCategory = (konto) => {
      if (!konto) return "Sonstige Einnahmen";
      const kontoNum = parseInt(konto);

      if (kontoNum >= 3200 && kontoNum < 3300) return "Bruttoerlöse Artikel";
      if (kontoNum >= 3400 && kontoNum < 3500)
        return "Bruttoerlöse Dienstleistung";
      if (kontoNum >= 3800 && kontoNum < 3900) return "Rabatte/Preisnachlässe";
      return "Sonstige Einnahmen";
    };

    entries.forEach((entry) => {
      // Ignoriere MwSt-Abrechnungen in der Auswertung
      if (entry.ist_mwst_abrechnung === true) return;

      const betrag = entry.brutto_betrag || entry.betrag || 0;
      const isIncome = entry.typ === "Einnahme";
      const targetSection = isIncome ? analysis.income : analysis.expense;

      // Bestimme Kategorie
      const category = isIncome
        ? getIncomeCategory(entry.konto)
        : getExpenseCategory(entry.konto, entry.projekt);

      // Initialisiere Kategorie
      if (!targetSection.byCategory[category]) {
        targetSection.byCategory[category] = {
          actual: 0,
          forecast: 0,
          total: 0,
        };
      }

      // Berechne Ist oder Prognose
      if (entry.typ_timing === "Einmalig") {
        const entryDate = new Date(entry.versand_datum || entry.wann);
        const entryYear = entryDate.getFullYear();

        // Zeitfilter für einmalige Einträge
        if (entryYear !== year) return;

        if (quarter) {
          const month = entryDate.getMonth();
          const startMonth = (quarter - 1) * 3;
          const endMonth = quarter * 3 - 1;
          if (month < startMonth || month > endMonth) return;
        }

        const isPast = entryDate <= today;

        if (isPast) {
          targetSection.byCategory[category].actual += betrag;
          targetSection.totalActual += betrag;
        } else {
          targetSection.byCategory[category].forecast += betrag;
          targetSection.totalForecast += betrag;
        }

        targetSection.byCategory[category].total += betrag;
        targetSection.total += betrag;
      } else {
        // Regelmässige Einträge
        const startDate = new Date(entry.ab_wann);
        const endDate = entry.bis_wann
          ? new Date(entry.bis_wann)
          : new Date(year + 10, 11, 31);

        // Prüfe ob Eintrag im gewählten Jahr/Quartal aktiv ist
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31, 23, 59, 59);

        if (endDate < yearStart || startDate > yearEnd) return;

        const rangeStart = new Date(
          Math.max(startDate.getTime(), yearStart.getTime())
        );
        const rangeEnd = new Date(
          Math.min(endDate.getTime(), yearEnd.getTime())
        );

        if (quarter) {
          const qStart = new Date(year, (quarter - 1) * 3, 1);
          const qEnd = new Date(year, quarter * 3, 0, 23, 59, 59);
          rangeStart.setTime(Math.max(rangeStart.getTime(), qStart.getTime()));
          rangeEnd.setTime(Math.min(rangeEnd.getTime(), qEnd.getTime()));

          if (rangeStart > rangeEnd) return;
        }

        const occurrences = calculateRecurringOccurrences(
          rangeStart,
          rangeEnd,
          entry.haeufigkeit
        );

        occurrences.forEach((occDate) => {
          const isPast = occDate <= today;

          if (isPast) {
            targetSection.byCategory[category].actual += betrag;
            targetSection.totalActual += betrag;
          } else {
            targetSection.byCategory[category].forecast += betrag;
            targetSection.totalForecast += betrag;
          }

          targetSection.byCategory[category].total += betrag;
          targetSection.total += betrag;
        });
      }
    });

    return analysis;
  } catch (error) {
    console.error("Fehler bei Kategorien-Prognose:", error);
    return {
      income: { byCategory: {}, totalActual: 0, totalForecast: 0, total: 0 },
      expense: { byCategory: {}, totalActual: 0, totalForecast: 0, total: 0 },
    };
  }
};

// Berechne Fälligkeitsdatum eines Budget-Eintrags
// wann = Rechnungsdatum, Fälligkeit = wann + zahlungsfrist (oder versand_datum + zahlungsfrist)
export const calculateDueDate = (entry) => {
  if (entry.versand_datum && entry.zahlungsfrist) {
    const d = new Date(entry.versand_datum);
    d.setDate(d.getDate() + parseInt(entry.zahlungsfrist));
    return d;
  }
  if (entry.wann && entry.zahlungsfrist && entry.typ_timing === "Einmalig") {
    const d = new Date(entry.wann);
    d.setDate(d.getDate() + parseInt(entry.zahlungsfrist));
    return d;
  }
  if (entry.wann) {
    return new Date(entry.wann);
  }
  return null;
};

// Berechne Status eines Budget-Eintrags (NEU für Overdue-Tracking)
export const calculateBudgetEntryStatus = (entry) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // === AUSGABEN (Expenses) ===
  if (entry.typ === "Ausgabe") {
    // Wenn "Automatische Zahlung" aktiviert ist, ignoriere Status-Tracking
    if (entry.automatic_payment === true) {
      return "automatic";
    }

    // Für wiederkehrende Einträge (ohne automatic_payment), zeige immer "expecting"
    // (Die tatsächliche Instanz-Status wird über Einmalig-Kopien gehandhabt)
    if (entry.typ_timing !== "Einmalig") {
      return entry.status || "expecting";
    }

    // Wenn bereits bezahlt, Status bleibt "paid"
    if (entry.status === "paid" || entry.paid_date) {
      return "paid";
    }

    // Wenn "Zahlung ausgelöst" wurde, prüfe ob Zahlungsdatum erreicht ist
    if (entry.status === "entered_payment" && entry.payment_date) {
      const paymentDate = new Date(entry.payment_date);
      paymentDate.setHours(0, 0, 0, 0);

      if (paymentDate <= today) {
        return "paid"; // Zahlungsdatum erreicht -> automatisch auf "paid"
      }
      return "entered_payment"; // Zahlungsdatum noch in der Zukunft
    }

    // Wenn Rechnung "erhalten" wurde
    if (entry.status === "received") {
      return "received";
    }

    // Prüfe Fälligkeitsdatum (wann + zahlungsfrist)
    const dueDate = calculateDueDate(entry);
    if (dueDate) {
      dueDate.setHours(0, 0, 0, 0);

      // Fälligkeitsdatum in der Vergangenheit und noch offen -> "overdue"
      // ("expected" zusaetzlich: Alt-Eintraege tragen den frueheren
      // Einnahmen-Default und blieben sonst ewig "expecting"; gespeichertes
      // "overdue" bleibt overdue statt auf "expecting" zurueckzufallen)
      if (
        dueDate < today &&
        (entry.status === "expecting" ||
          entry.status === "expected" ||
          entry.status === "overdue" ||
          !entry.status)
      ) {
        return "overdue";
      }
    }

    // Ansonsten: expecting
    return "expecting";
  }

  // === EINNAHMEN (Income) - Bestehende Logik ===
  if (entry.typ === "Einnahme") {
    // Wenn bereits bezahlt, Status bleibt "paid"
    if (entry.status === "paid" || entry.paid_date) {
      return "paid";
    }

    // Nur für einmalige Einträge relevant
    if (entry.typ_timing !== "Einmalig") {
      return entry.status || "expected";
    }

    const dueDate = calculateDueDate(entry);
    if (dueDate) {
      dueDate.setHours(0, 0, 0, 0);

      // Fälligkeitsdatum ist heute oder in der Vergangenheit -> immer "overdue"
      if (dueDate <= today) {
        return "overdue";
      }
    }

    // Fälligkeitsdatum liegt in der Zukunft
    // Prüfe ob manuell als "sent" markiert
    if (entry.status === "sent") {
      return "sent";
    }

    // Prüfe ob Versanddatum in der Vergangenheit liegt (aber nicht als "sent" markiert)
    if (entry.versand_datum) {
      const sendDate = new Date(entry.versand_datum);
      sendDate.setHours(0, 0, 0, 0);

      if (sendDate <= today) {
        return "delayed"; // Versanddatum überschritten, aber nicht als "sent" markiert
      }
    }

    // Ansonsten: expected
    return "expected";
  }

  return entry.status || "expected";
};

// Hilfsfunktion: Finde den nächsten Montag (Start der nächsten Kalenderwoche)
const getNextMonday = (fromDate = new Date()) => {
  const date = new Date(fromDate);
  date.setHours(0, 0, 0, 0);
  const dayOfWeek = date.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek; // Sonntag = 0
  date.setDate(date.getDate() + daysUntilMonday);
  return date;
};

// Hilfsfunktion: Berechne die nächste Instanz eines wiederkehrenden Eintrags
const getNextRecurringInstance = (entry, fromDate = new Date()) => {
  if (entry.typ_timing !== "Regelmässig") {
    return null;
  }

  const today = new Date(fromDate);
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(entry.ab_wann);
  startDate.setHours(0, 0, 0, 0);

  const endDate = entry.bis_wann ? new Date(entry.bis_wann) : null;
  if (endDate) {
    endDate.setHours(0, 0, 0, 0);
  }

  // Extrahiere Tag aus ab_wann (z.B. 25 für den 25. des Monats)
  const dayOfMonth = startDate.getDate();

  // Finde die nächste Instanz basierend auf Häufigkeit
  let nextInstance = new Date(startDate);

  if (entry.haeufigkeit === "monatlich") {
    // Setze auf den aktuellen oder nächsten Monat
    nextInstance = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);

    // Wenn dieses Datum bereits in der Vergangenheit liegt, nimm den nächsten Monat
    if (nextInstance <= today) {
      nextInstance = new Date(today.getFullYear(), today.getMonth() + 1, dayOfMonth);
    }
  } else if (entry.haeufigkeit === "jährlich") {
    // Setze auf das aktuelle oder nächste Jahr
    nextInstance = new Date(today.getFullYear(), startDate.getMonth(), dayOfMonth);

    if (nextInstance <= today) {
      nextInstance = new Date(today.getFullYear() + 1, startDate.getMonth(), dayOfMonth);
    }
  } else if (entry.haeufigkeit === "wöchentlich") {
    // Finde den gleichen Wochentag
    const dayOfWeek = startDate.getDay();
    nextInstance = new Date(today);
    const daysUntilNext = (dayOfWeek - today.getDay() + 7) % 7;
    nextInstance.setDate(today.getDate() + (daysUntilNext === 0 ? 7 : daysUntilNext));
  }

  // Prüfe ob die nächste Instanz nach dem Enddatum liegt
  if (endDate && nextInstance > endDate) {
    return null;
  }

  return nextInstance;
};

// Hilfsfunktion: Formatiere Periode (Jahr-Monat)
const formatInstancePeriod = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

// Hilfsfunktion: Formatiere Monat/Jahr für Namen (z.B. "November 2025")
const formatMonthYear = (date) => {
  const monthNames = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

// Hilfsfunktion: Hole Informationen zur aktuellen Instanz eines wiederkehrenden Eintrags
// Gibt zurück: { instanceDate, instancePeriod, oneTimeCopy (if exists), displayStatus }
export const getCurrentRecurringInstanceInfo = async (recurringEntry) => {
  if (recurringEntry.typ_timing !== "Regelmässig") {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Berechne die aktuelle (oder nächste) Instanz
  const currentInstance = getNextRecurringInstance(recurringEntry, today);
  if (!currentInstance) {
    return null; // Keine zukünftige Instanz mehr
  }

  const instancePeriod = formatInstancePeriod(currentInstance);

  // Suche nach einer existierenden Einmalig-Kopie für diese Periode
  const entries = await getBudgetEntries();
  const oneTimeCopy = entries.find(
    (e) =>
      e.created_from_recurring_id === recurringEntry.id &&
      e.instance_period === instancePeriod
  );

  // Wenn eine Kopie existiert, zeige deren Status
  // Ansonsten zeige "expecting" für die aktuelle Instanz
  let displayStatus = "expecting";
  if (oneTimeCopy) {
    displayStatus = calculateBudgetEntryStatus(oneTimeCopy);
  } else {
    // Prüfe ob die aktuelle Instanz bereits überfällig ist
    currentInstance.setHours(0, 0, 0, 0);
    if (currentInstance < today) {
      displayStatus = "overdue";
    }
  }

  return {
    instanceDate: currentInstance,
    instancePeriod,
    oneTimeCopy,
    displayStatus,
  };
};

// Verschiebe überfällige "expecting" Ausgaben zur nächsten KW
// Und aktualisiere "entered_payment" Ausgaben zu "paid" wenn Zahlungsdatum erreicht
// Und erstelle Einmalig-Kopien für überfällige wiederkehrende Ausgaben
export const moveOverdueExpensesToNextKW = async () => {
  try {
    const entries = await getBudgetEntries();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 1. Finde überfällige "expecting" EINMALIGE Ausgaben
    const overdueOneTimeExpenses = entries.filter((entry) => {
      // Nur Ausgaben
      if (entry.typ !== "Ausgabe") return false;
      // Nur einmalige Einträge (nicht von recurring erstellt)
      if (entry.typ_timing !== "Einmalig") return false;
      if (entry.created_from_recurring_id) return false; // Überspringen von erstellten Kopien
      // Nicht für automatische Zahlungen
      if (entry.automatic_payment === true) return false;
      // Nur "expecting" Status
      if (entry.status && entry.status !== "expecting") return false;

      // Prüfe ob Fälligkeitsdatum in der Vergangenheit liegt
      const dueDate = calculateDueDate(entry);
      if (!dueDate) return false;
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    });

    // 2. Finde "entered_payment" Ausgaben, deren Zahlungsdatum erreicht ist
    const paidExpenses = entries.filter((entry) => {
      // Nur Ausgaben
      if (entry.typ !== "Ausgabe") return false;
      // Nur einmalige Einträge
      if (entry.typ_timing !== "Einmalig") return false;
      // Nur "entered_payment" Status
      if (entry.status !== "entered_payment") return false;
      // Mit payment_date gesetzt
      if (!entry.payment_date) return false;

      // Prüfe ob Zahlungsdatum erreicht oder überschritten ist
      const paymentDate = new Date(entry.payment_date);
      paymentDate.setHours(0, 0, 0, 0);
      return paymentDate <= today;
    });

    // 3. Finde WIEDERKEHRENDE Ausgaben deren aktuelle Instanz überfällig ist
    const overdueRecurringExpenses = entries.filter((entry) => {
      // Nur Ausgaben
      if (entry.typ !== "Ausgabe") return false;
      // Nur wiederkehrende Einträge
      if (entry.typ_timing !== "Regelmässig") return false;
      // Nicht für automatische Zahlungen
      if (entry.automatic_payment === true) return false;

      // Berechne die aktuelle Instanz
      const currentInstance = getNextRecurringInstance(entry, today);
      if (!currentInstance) return false; // Keine zukünftige Instanz mehr

      // Prüfe ob die aktuelle Instanz in der Vergangenheit liegt
      currentInstance.setHours(0, 0, 0, 0);
      if (currentInstance >= today) return false; // Noch nicht fällig

      // Prüfe ob bereits eine Einmalig-Kopie für diese Periode existiert
      const instancePeriod = formatInstancePeriod(currentInstance);
      const existingCopy = entries.find(
        (e) =>
          e.created_from_recurring_id === entry.id &&
          e.instance_period === instancePeriod
      );

      return !existingCopy; // Nur wenn noch keine Kopie existiert
    });

    const updatePromises = [];
    const createPromises = [];

    // Verschiebe überfällige EINMALIGE Ausgaben zur nächsten KW
    overdueOneTimeExpenses.forEach((entry) => {
      const nextMonday = getNextMonday(today);
      const updatedEntry = {
        ...entry,
        wann: nextMonday.toISOString().split("T")[0],
        status: "expecting", // Reset to expecting
      };
      updatePromises.push(saveBudgetEntry(updatedEntry));
    });

    // Markiere Ausgaben als bezahlt
    paidExpenses.forEach((entry) => {
      const updatedEntry = {
        ...entry,
        status: "paid",
        paid_date: entry.payment_date, // Setze paid_date auf payment_date
      };
      updatePromises.push(saveBudgetEntry(updatedEntry));
    });

    // Erstelle EINMALIG-Kopien für überfällige wiederkehrende Ausgaben
    overdueRecurringExpenses.forEach((entry) => {
      const currentInstance = getNextRecurringInstance(entry, today);
      const instancePeriod = formatInstancePeriod(currentInstance);
      const monthYearName = formatMonthYear(currentInstance);

      const oneTimeCopy = {
        typ: entry.typ,
        name: `${entry.name} (${monthYearName})`,
        konto: entry.konto,
        typ_timing: "Einmalig",
        wann: currentInstance.toISOString().split("T")[0],
        betrag: entry.betrag,
        projekt: entry.projekt || "",
        phase: entry.phase || "",
        mwst_satz: entry.mwst_satz,
        ist_brutto: entry.ist_brutto,
        ist_prognose: entry.ist_prognose,
        brutto_betrag: entry.brutto_betrag,
        netto_betrag: entry.netto_betrag,
        mwst_betrag: entry.mwst_betrag,
        status: "overdue", // Startet als überfällig
        created_from_recurring_id: entry.id,
        instance_period: instancePeriod,
        automatic_payment: false,
      };

      createPromises.push(saveBudgetEntry(oneTimeCopy));
    });

    const results = await Promise.all([...updatePromises, ...createPromises]);
    return results;
  } catch (error) {
    console.error("Fehler beim Verschieben überfälliger Ausgaben:", error);
    return [];
  }
};

// ========== LOHN-BUDGET INTEGRATION ==========

// Get all budget entries linked to a team member
export const getBudgetEntriesForMember = async (teamMemberId) => {
  const entries = await getBudgetEntries();
  return entries.filter((e) => e.team_member_id === teamMemberId);
};

// Get budget entry linked to a specific salary entry
export const getBudgetEntryForSalary = async (salaryEntryId) => {
  const entries = await getBudgetEntries();
  return entries.find((e) => e.lohn_eintrag_id === salaryEntryId) || null;
};

// Create a recurring salary budget entry
export const createSalaryBudgetEntry = async ({
  teamMemberId,
  salaryEntryId,
  memberName,
  betrag,
  brutto,
  abWann,
  bisWann,
}) => {
  const amount = betrag ?? brutto ?? 0;
  return saveBudgetEntry({
    typ: "Ausgabe",
    name: `Lohn ${memberName}`,
    konto: "5000",
    typ_timing: "Regelmässig",
    haeufigkeit: "monatlich",
    ab_wann: abWann,
    bis_wann: bisWann || "",
    betrag: amount,
    brutto_betrag: amount,
    netto_betrag: amount,
    mwst_betrag: 0,
    mwst_satz: 0,
    ist_brutto: true,
    automatic_payment: false,
    team_member_id: teamMemberId,
    lohn_eintrag_id: salaryEntryId,
    deletable_with_member: true,
  });
};

// Create a one-time onboarding budget entry
export const createOnboardingBudgetEntry = async ({
  teamMemberId,
  memberName,
  itemName,
  betrag,
  datum,
}) => {
  return saveBudgetEntry({
    typ: "Ausgabe",
    name: `${itemName} ${memberName}`,
    konto: "6300",
    typ_timing: "Einmalig",
    wann: datum,
    betrag: betrag,
    brutto_betrag: betrag,
    netto_betrag: betrag,
    mwst_betrag: 0,
    mwst_satz: 0,
    ist_brutto: true,
    team_member_id: teamMemberId,
    deletable_with_member: true,
  });
};

// Calculate the weighted 13th month salary for a given November
// Reference period: Dec(Y-1) through Nov(Y) = 12 months
// For each month, the active salary (latest abDatum <= that month) is summed
// Result = totalSalary / 12
// Uses Nettolohn (after AN deductions) when insurance rates + member data available
const calculate13thMonthAmount = (allSalaryEntries, memberStart, memberEnd, targetYear, memberData, insuranceRates, pkManuell) => {
  // 12 months: Dec(Y-1), Jan(Y), Feb(Y), ..., Nov(Y)
  const refMonths = [{ year: targetYear - 1, month: 11 }];
  for (let m = 0; m <= 10; m++) refMonths.push({ year: targetYear, month: m });

  const startDate = new Date(memberStart);
  let totalSalary = 0;

  for (const { year, month } of refMonths) {
    const monthLastDay = new Date(year, month + 1, 0);

    // Skip if member wasn't employed yet
    if (startDate > monthLastDay) continue;
    // Skip if member had already left
    if (memberEnd) {
      const endDate = new Date(memberEnd);
      if (new Date(year, month, 1) > endDate) continue;
    }

    // Find active salary: latest entry where abDatum <= last day of month
    const monthEndStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(monthLastDay.getDate()).padStart(2, "0")}`;
    // allSalaryEntries sorted by abDatum desc (newest first)
    const activeSalary = allSalaryEntries.find((e) => e.abDatum <= monthEndStr);

    if (activeSalary) {
      const brutto = activeSalary.brutto || 0;
      const netto = activeSalary.netto || 0;
      const pensum = activeSalary.pensum ?? 100;
      const effBrutto = (brutto * pensum) / 100;

      // Compute Nettolohn (after deductions) if possible
      if (netto > 0) {
        totalSalary += (netto * pensum) / 100;
      } else if (insuranceRates && memberData?.geschlecht && memberData?.geburtsdatum) {
        const alter = getAgeAtDate(memberData.geburtsdatum, monthLastDay);
        const deductions = calcAllDeductions(effBrutto, memberData.geschlecht, alter, insuranceRates, { pkManuell });
        totalSalary += deductions.netto > 0 ? deductions.netto : effBrutto;
      } else {
        totalSalary += effBrutto;
      }
    }
  }

  return Math.round((totalSalary / 12) * 100) / 100;
};

// Create 13th month salary budget entries (additional to regular monthly salary)
// Uses weighted average of all salary entries across the Dec-Nov reference period
export const create13thMonthBudgetEntries = async ({
  teamMemberId,
  salaryEntryId,
  memberName,
  currentAmount,
  memberStart,
  allSalaryEntries,
  bisWann,
  memberData,
  insuranceRates,
  pkManuell,
}) => {
  const now = new Date();

  // Target November 28: next upcoming November that isn't in the past
  let targetYear = now.getMonth() <= 10 ? now.getFullYear() : now.getFullYear() + 1;

  // Calculate weighted 13th month for the upcoming November (uses Nettolohn)
  const weightedAmount = calculate13thMonthAmount(
    allSalaryEntries, memberStart, bisWann || null, targetYear, memberData, insuranceRates, pkManuell
  );

  const novDate = `${targetYear}-11-28`;
  const promises = [];

  if (weightedAmount > 0) {
    // One-time entry for this November with the calculated weighted amount
    promises.push(
      saveBudgetEntry({
        typ: "Ausgabe",
        name: `13. Monatslohn ${memberName}`,
        konto: "5000",
        typ_timing: "Einmalig",
        wann: novDate,
        betrag: weightedAmount,
        brutto_betrag: weightedAmount,
        netto_betrag: weightedAmount,
        mwst_betrag: 0,
        mwst_satz: 0,
        ist_brutto: true,
        automatic_payment: false,
        team_member_id: teamMemberId,
        lohn_eintrag_id: `${salaryEntryId}_13p`,
        deletable_with_member: true,
      })
    );
  }

  // Recurring yearly entry for future Novembers (full current salary)
  if (currentAmount > 0) {
    const nextNovDate = `${targetYear + 1}-11-28`;
    promises.push(
      saveBudgetEntry({
        typ: "Ausgabe",
        name: `13. Monatslohn ${memberName}`,
        konto: "5000",
        typ_timing: "Regelmässig",
        haeufigkeit: "jährlich",
        ab_wann: nextNovDate,
        bis_wann: bisWann || "",
        betrag: currentAmount,
        brutto_betrag: currentAmount,
        netto_betrag: currentAmount,
        mwst_betrag: 0,
        mwst_satz: 0,
        ist_brutto: true,
        automatic_payment: false,
        team_member_id: teamMemberId,
        lohn_eintrag_id: `${salaryEntryId}_13`,
        deletable_with_member: true,
      })
    );
  }

  return Promise.all(promises);
};

// Delete all 13th month budget entries linked to a salary entry
export const delete13thMonthBudgetEntries = async (salaryEntryId) => {
  const allEntries = await getBudgetEntries();
  const toDelete = allEntries.filter(
    (e) =>
      e.lohn_eintrag_id === `${salaryEntryId}_13` ||
      e.lohn_eintrag_id === `${salaryEntryId}_13p`
  );

  // Also delete one-time copies created from recurring 13th month entries
  const recurringIds = toDelete
    .filter((e) => e.typ_timing === "Regelmässig")
    .map((e) => e.id);
  const oneTimeCopies = allEntries.filter(
    (e) =>
      e.created_from_recurring_id &&
      recurringIds.includes(e.created_from_recurring_id)
  );

  const all = [...toDelete, ...oneTimeCopies];
  await Promise.all(all.map((e) => deleteBudgetEntry(e.id)));
  return all.length;
};

// Update bis_wann on a salary budget entry (when salary changes or employment ends)
export const updateSalaryBudgetEndDate = async (salaryEntryId, bisWann) => {
  const entry = await getBudgetEntryForSalary(salaryEntryId);
  if (!entry) return null;

  return saveBudgetEntry({
    ...entry,
    bis_wann: bisWann,
  });
};

// Delete all budget entries linked to a team member that are deletable
export const deleteMemberBudgetEntries = async (teamMemberId) => {
  const entries = await getBudgetEntriesForMember(teamMemberId);
  const deletableEntries = entries.filter((e) => e.deletable_with_member);

  // Also delete one-time copies created from recurring salary entries
  const allEntries = await getBudgetEntries();
  const recurringIds = deletableEntries
    .filter((e) => e.typ_timing === "Regelmässig")
    .map((e) => e.id);
  const oneTimeCopies = allEntries.filter(
    (e) => e.created_from_recurring_id && recurringIds.includes(e.created_from_recurring_id)
  );

  const toDelete = [...deletableEntries, ...oneTimeCopies];
  await Promise.all(toDelete.map((e) => deleteBudgetEntry(e.id)));
  return toDelete.length;
};

// Namens-Sync bei Umbenennung eines Team-Mitglieds: Budget-Eintrags-Namen
// ("Lohn X", "13. Monatslohn X", Onboarding) werden nur bei der ERSTELLUNG
// gesetzt — ohne Sync trugen sie nach einer Umbenennung dauerhaft den alten
// Namen (so blieb "Lohn Research Person" nach der Umbenennung in Nina D.).
// Erfasst auch Einmalig-Kopien (kein team_member_id, aber
// created_from_recurring_id auf einen Eintrag des Mitglieds).
export const renameMemberBudgetEntries = async (teamMemberId, oldName, newName) => {
  if (!teamMemberId || !oldName || !newName || oldName === newName) return 0;

  const entries = await getBudgetEntries();
  const memberIds = new Set(
    entries.filter((e) => e.team_member_id === teamMemberId).map((e) => e.id)
  );
  const affected = entries.filter(
    (e) =>
      e.name?.includes(oldName) &&
      (e.team_member_id === teamMemberId ||
        (e.created_from_recurring_id && memberIds.has(e.created_from_recurring_id)))
  );

  await Promise.all(
    affected.map((e) =>
      saveBudgetEntry({
        ...e,
        name: e.name.split(oldName).join(newName),
        // betrag in der Konvention des Formulars uebergeben (brutto bei
        // ist_brutto), sonst wuerde die MwSt-Neuberechnung den Betrag schrumpfen
        betrag: e.ist_brutto !== false ? e.brutto_betrag ?? e.betrag : e.betrag,
      })
    )
  );
  return affected.length;
};

// Set bis_wann on all active salary budget entries for a member (employment end)
export const endMemberSalaryBudgets = async (teamMemberId, endDate) => {
  const entries = await getBudgetEntriesForMember(teamMemberId);
  const salaryEntries = entries.filter(
    (e) => e.lohn_eintrag_id && e.typ_timing === "Regelmässig" && !e.bis_wann
  );

  await Promise.all(
    salaryEntries.map((e) =>
      saveBudgetEntry({ ...e, bis_wann: endDate })
    )
  );
  return salaryEntries.length;
};

// ========== INSURANCE BUDGET INTEGRATION ==========

// Recalculate the aggregated social insurance budget entry (AN+AG).
// Die Lohn-Budgets stehen als Netto in den Finanzen; die Firma zahlt den Kassen
// aber AN- UND AG-Anteile. Netto + (AN+AG) = Brutto + AG, damit ist der
// Cashflow vollstaendig. FAK laeuft separat (recalculateFAKBudget).
// lohn_eintrag_id format: ins_all_{year}
import { calcAllDeductions, calcFAKNetto, getAgeAtDate } from "../utils/insuranceCalculations";
import { getAllPkManualEntries, getCurrentSalary } from "./azureSalaryService";
import { getAllTeamMembers } from "./azureTeamService";

// Aktive Mitglieder mit aktuellem Lohn laden. Team und Loehne werden hier
// selbst geladen: Aufrufer uebergaben frueher Teillisten (einzelnes Mitglied
// aus dem TeamDetailModal), was das firmenweite Aggregat auf eine Person
// schrumpfen liess.
const loadActiveSalaryBasis = async () => {
  const todayStr = new Date().toISOString().split("T")[0];
  const teamMembers = await getAllTeamMembers();
  const active = teamMembers.filter(
    (m) => m.start && m.start <= todayStr && (!m.ende || m.ende >= todayStr)
  );

  const basis = [];
  await Promise.all(
    active.map(async (member) => {
      const salary = await getCurrentSalary(member.employeeUuid);
      if (!salary) return;
      // Abgaben rechnen auf Brutto; Netto nur als Fallback wenn kein Brutto erfasst
      const lohn = salary.brutto || salary.netto || 0;
      const pensum = salary.pensum ?? member.pensum ?? 100;
      const effektiverBrutto = (lohn * pensum) / 100;
      if (effektiverBrutto > 0) {
        basis.push({
          member,
          effektiverBrutto,
          dreizehnter: salary.dreizehnter === true,
        });
      }
    })
  );
  return basis;
};

// Aggregat-Templates duerfen aktualisiert werden solange sie nicht manuell
// abgearbeitet wurden. saveBudgetEntry speichert neue Eintraege mit Status
// "expected" — die alte Bedingung (=== "expecting") traf deshalb nie zu und
// fror das Aggregat auf dem Erstellungsstand ein.
const isAggregateUpdatable = (entry) =>
  !entry.status || entry.status === "expected" || entry.status === "expecting";

export const recalculateInsuranceBudget = async (insuranceRates) => {
  if (!insuranceRates) return;

  const today = new Date();
  const currentYear = today.getFullYear();

  const basis = await loadActiveSalaryBasis();
  if (!basis.length) return;

  // Manual PK mode: load per-person contributions once (keyed by employeeUuid)
  let pkManualMap = {};
  if (insuranceRates.pk?.berechnungsModus === "manuell") {
    const pkEntries = await getAllPkManualEntries();
    pkEntries.forEach((e) => { pkManualMap[e.teamMemberId] = e; });
  }

  // Total monthly AN+AG across all active members.
  // total13 = Abgaben auf dem 13. Monatslohn: eine ZUSÄTZLICHE Monatsbasis
  // AN+AG für Personen mit aktiviertem 13. ML — hier INKL. FAK-AG-Beitrag
  // (die Zulagen-Verrechnung läuft nur monatlich, nicht 13x).
  let totalMonthly = 0;
  let total13 = 0;

  for (const { member, effektiverBrutto, dreizehnter } of basis) {
    const alter = getAgeAtDate(member.geburtsdatum, today);
    const result = calcAllDeductions(effektiverBrutto, member.geschlecht, alter, insuranceRates, {
      pkManuell: pkManualMap[member.employeeUuid],
    });

    for (const d of result.deductions) {
      if (d.type === "fak") {
        // FAK hat einen eigenen monatlichen Budget-Eintrag; nur der 13.-ML-Anteil
        // (reiner AG-Beitrag) fliesst in den jährlichen 13.-ML-Abgaben-Eintrag
        if (dreizehnter) total13 += d.agKosten;
        continue;
      }
      totalMonthly += d.anAbzug + d.agKosten;
      if (dreizehnter) total13 += d.anAbzug + d.agKosten;
    }
  }

  if (totalMonthly <= 0) return;

  // Load existing insurance budget entries
  const allEntries = await getBudgetEntries();
  const existingInsEntries = allEntries.filter(
    (e) => e.lohn_eintrag_id?.startsWith("ins_") && e.team_member_id === "insurance_aggregated"
  );

  const entryId = `ins_all_${currentYear}`;
  const entryName = "Sozialversicherungen (AN+AG)";

  // Check zahlungsrhythmus from insurance rates
  // Use the most common rhythm, default to monatlich
  const rhythmus = insuranceRates.ahv?.zahlungsrhythmus || "monatlich";

  // Calculate betrag based on rhythm
  let betrag = totalMonthly;
  let haeufigkeit = "monatlich";
  if (rhythmus === "quartalsweise") {
    betrag = totalMonthly * 3;
    haeufigkeit = "quartalsweise";
  } else if (rhythmus === "jaehrlich") {
    betrag = totalMonthly * 12;
    haeufigkeit = "jährlich";
  }

  betrag = Math.round(betrag * 100) / 100;

  // Find existing entry
  const existing = existingInsEntries.find((e) => e.lohn_eintrag_id === entryId);

  if (existing) {
    if (
      isAggregateUpdatable(existing) &&
      (Math.abs(existing.betrag - betrag) > 0.5 || existing.name !== entryName)
    ) {
      // MwSt-Felder explizit auf 0: Alt-Eintraege tragen teils satz 8.1 —
      // saveBudgetEntry wuerde den Betrag sonst als Brutto reinterpretieren
      await saveBudgetEntry({
        ...existing,
        name: entryName,
        betrag,
        brutto_betrag: betrag,
        netto_betrag: betrag,
        mwst_betrag: 0,
        mwst_satz: 0,
        ist_brutto: true,
        haeufigkeit,
      });
    }
  } else {
    // Jahreswechsel: Vorjahres-Aggregat schliessen, sonst laeuft es parallel weiter
    const prevEntry = existingInsEntries.find(
      (e) => e.lohn_eintrag_id === `ins_all_${currentYear - 1}` && e.typ_timing === "Regelmässig" && !e.bis_wann
    );
    if (prevEntry) {
      await saveBudgetEntry({ ...prevEntry, bis_wann: `${currentYear - 1}-12-31` });
    }

    // Create new recurring entry
    // If we're past Jan, start from next upcoming month's 26th
    let startDate = new Date(currentYear, 0, 26);
    if (startDate < today) {
      startDate = new Date(today.getFullYear(), today.getMonth(), 26);
      if (startDate < today) startDate.setMonth(startDate.getMonth() + 1);
    }

    await saveBudgetEntry({
      typ: "Ausgabe",
      name: entryName,
      konto: "5700",
      typ_timing: "Regelmässig",
      haeufigkeit,
      ab_wann: startDate.toISOString().split("T")[0],
      bis_wann: "",
      betrag,
      brutto_betrag: betrag,
      netto_betrag: betrag,
      mwst_betrag: 0,
      mwst_satz: 0,
      ist_brutto: true,
      automatic_payment: false,
      team_member_id: "insurance_aggregated",
      lohn_eintrag_id: entryId,
      deletable_with_member: false,
    });
  }

  // ===== Abgaben auf dem 13. Monatslohn (jährlicher Eintrag per 28.11.) =====
  // Modell: eine zusätzliche Monatsbasis AN+AG (inkl. FAK-AG) für alle
  // Personen mit aktiviertem 13. ML — fällig zusammen mit dessen Auszahlung.
  const betrag13 = Math.round(total13 * 100) / 100;
  const entry13Name = "Sozialabgaben 13. Monatslohn (AN+AG)";
  const entry13 = allEntries.find(
    (e) =>
      e.lohn_eintrag_id === "ins_all_13" &&
      e.team_member_id === "insurance_aggregated"
  );

  if (betrag13 <= 0) {
    // Niemand (mehr) mit 13. Monatslohn -> Eintrag entfernen
    if (entry13 && isAggregateUpdatable(entry13)) {
      await deleteBudgetEntry(entry13.id);
    }
    return;
  }

  if (entry13) {
    if (
      isAggregateUpdatable(entry13) &&
      (Math.abs(entry13.betrag - betrag13) > 0.5 || entry13.name !== entry13Name)
    ) {
      await saveBudgetEntry({
        ...entry13,
        name: entry13Name,
        betrag: betrag13,
        brutto_betrag: betrag13,
        netto_betrag: betrag13,
        mwst_betrag: 0,
        mwst_satz: 0,
        ist_brutto: true,
      });
    }
  } else {
    // Liegt der 28.11. dieses Jahres in der Vergangenheit, startet die Serie
    // erst im nächsten Jahr (jährlich wiederkehrend, unbegrenzt)
    const novemberDatum =
      today <= new Date(currentYear, 10, 28)
        ? `${currentYear}-11-28`
        : `${currentYear + 1}-11-28`;

    await saveBudgetEntry({
      typ: "Ausgabe",
      name: entry13Name,
      konto: "5700",
      typ_timing: "Regelmässig",
      haeufigkeit: "jährlich",
      ab_wann: novemberDatum,
      bis_wann: "",
      betrag: betrag13,
      brutto_betrag: betrag13,
      netto_betrag: betrag13,
      mwst_betrag: 0,
      mwst_satz: 0,
      ist_brutto: true,
      automatic_payment: false,
      team_member_id: "insurance_aggregated",
      lohn_eintrag_id: "ins_all_13",
      deletable_with_member: false,
    });
  }
};

// Recalculate FAK netto budget (AG-Beitrag minus Zulagen, separate from aggregated entry)
export const recalculateFAKBudget = async (fakRates) => {
  if (!fakRates) return;

  const today = new Date();
  const currentYear = today.getFullYear();

  const basis = await loadActiveSalaryBasis();
  if (!basis.length) return;

  let totalMonthlyNetto = 0;

  for (const { member, effektiverBrutto } of basis) {
    const result = calcFAKNetto(effektiverBrutto, member.kinder || [], fakRates, today);
    totalMonthlyNetto += result.netto;
  }

  // Determine rhythm
  const rhythmus = fakRates.zahlungsrhythmus || "monatlich";
  let betrag = totalMonthlyNetto;
  let haeufigkeit = "monatlich";
  if (rhythmus === "quartalsweise") {
    betrag = totalMonthlyNetto * 3;
    haeufigkeit = "quartalsweise";
  } else if (rhythmus === "jaehrlich") {
    betrag = totalMonthlyNetto * 12;
    haeufigkeit = "jährlich";
  }

  betrag = Math.round(betrag * 100) / 100;

  const entryId = `ins_fak_netto_${currentYear}`;

  // Load existing FAK budget entries
  const allEntries = await getBudgetEntries();
  const fakEntries = allEntries.filter((e) => e.team_member_id === "fak_aggregated");
  const existing = fakEntries.find((e) => e.lohn_eintrag_id === entryId);

  // If betrag is 0, delete existing entry
  if (Math.abs(betrag) < 0.01) {
    if (existing) await deleteBudgetEntry(existing.id);
    return;
  }

  const absBetrag = Math.abs(betrag);
  const typ = betrag > 0 ? "Ausgabe" : "Einnahme";
  const name = betrag > 0 ? "FAK Beitrag (netto)" : "FAK Zulagen-Ueberschuss";

  if (existing) {
    if (
      isAggregateUpdatable(existing) &&
      (Math.abs(existing.betrag - absBetrag) > 0.5 || existing.typ !== typ)
    ) {
      await saveBudgetEntry({
        ...existing,
        typ,
        name,
        betrag: absBetrag,
        brutto_betrag: absBetrag,
        netto_betrag: absBetrag,
        mwst_betrag: 0,
        mwst_satz: 0,
        ist_brutto: true,
        haeufigkeit,
      });
    }
  } else {
    // Jahreswechsel: Vorjahres-Aggregat schliessen, sonst laeuft es parallel weiter
    const prevEntry = fakEntries.find(
      (e) => e.lohn_eintrag_id === `ins_fak_netto_${currentYear - 1}` && e.typ_timing === "Regelmässig" && !e.bis_wann
    );
    if (prevEntry) {
      await saveBudgetEntry({ ...prevEntry, bis_wann: `${currentYear - 1}-12-31` });
    }

    let startDate = new Date(currentYear, 0, 26);
    if (startDate < today) {
      startDate = new Date(today.getFullYear(), today.getMonth(), 26);
      if (startDate < today) startDate.setMonth(startDate.getMonth() + 1);
    }

    await saveBudgetEntry({
      typ,
      name,
      konto: "5700",
      typ_timing: "Regelmässig",
      haeufigkeit,
      ab_wann: startDate.toISOString().split("T")[0],
      bis_wann: "",
      betrag: absBetrag,
      brutto_betrag: absBetrag,
      netto_betrag: absBetrag,
      mwst_betrag: 0,
      mwst_satz: 0,
      ist_brutto: true,
      automatic_payment: false,
      team_member_id: "fak_aggregated",
      lohn_eintrag_id: entryId,
      deletable_with_member: false,
    });
  }
};

// Finde passende Budget-Einträge für eine Buchung (NEU für Matching)
export const findMatchingBudgetEntries = async (booking) => {
  try {
    const entries = await getBudgetEntries();
    const bookingAmount = Math.abs(booking.Betrag);

    // Filter: Nur Einnahmen, einmalig, nicht bezahlt
    const candidates = entries.filter((entry) => {
      if (entry.typ !== "Einnahme") return false;
      if (entry.typ_timing !== "Einmalig") return false;
      if (entry.status === "paid" || entry.paid_date) return false;

      const entryAmount = entry.brutto_betrag || entry.betrag || 0;
      const amountDiff = Math.abs(entryAmount - bookingAmount);

      // Toleranz von 1 CHF für Rundungsdifferenzen
      return amountDiff <= 1;
    });

    return candidates;
  } catch (error) {
    console.error("Fehler beim Suchen passender Budget-Einträge:", error);
    return [];
  }
};
